import { useState } from 'react';
import type { LabelUsage, EventActivationMeta } from '../../../shared/types';
import { LABEL_USAGE_OPTIONS, EVENT_AUDIENCE_OPTIONS, EVENT_SUPPORT_OPTIONS } from '../../../shared/types';
import { useAuth } from '../../contexts/AuthContext';
import CopyButton from '../ui/CopyButton';

/* Accordéon « Votre demande / question » récapitulant l'entrée utilisateur.
 * Extrait tel quel de ResultsDisplay.tsx. */

export default function OriginalInputAccordion({ title, input, context, images, labelUsage, event }: { title: string; input?: string; context?: string; images?: string[]; labelUsage?: LabelUsage; event?: EventActivationMeta }) {
  const [open, setOpen] = useState(false);
  const { features } = useAuth();
  const hasContent = !!(input || context || (images && images.length > 0) || labelUsage || event);
  if (!hasContent) return null;
  const labelUsageMeta = labelUsage
    ? LABEL_USAGE_OPTIONS.find((o) => o.value === labelUsage)
    : undefined;
  const audienceMeta = event
    ? EVENT_AUDIENCE_OPTIONS.find((o) => o.value === event.audience)
    : undefined;

  // Plain-markdown recap of what the user submitted — lets them copy the whole
  // card (e.g. to email "voilà ce que j'ai donné à Visaly et voici le verdict").
  const recapLines: string[] = [`**${title}**`];
  if (input) recapLines.push('', input);
  if (context) recapLines.push('', '**Contexte supplémentaire**', context);
  if (labelUsageMeta) recapLines.push('', '**Usage déclaré**', labelUsageMeta.label);
  if (event) {
    const op = [event.eventName, event.eventLocation, event.eventDate].filter(Boolean).join(' · ');
    if (op) recapLines.push('', '**Opération**', op);
    if (audienceMeta) recapLines.push('', '**Audience déclarée**', audienceMeta.label);
    if (event.supports.length > 0) {
      recapLines.push('', "**Supports de l'opération**");
      event.supports.forEach((s) => {
        const supportMeta = EVENT_SUPPORT_OPTIONS.find((o) => o.value === s);
        const detail = event.supportDetails?.[s];
        recapLines.push(`- ${supportMeta?.label ?? s}${detail ? ` : ${detail}` : ''}`);
      });
    }
  }
  if (images && images.length > 0) {
    recapLines.push('', `_${images.length} image${images.length > 1 ? 's' : ''} jointe${images.length > 1 ? 's' : ''}_`);
  }
  const recapText = recapLines.join('\n');

  return (
    <div className="mb-6 rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center">
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 flex items-center gap-2.5 px-5 py-3 text-left text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <svg className="w-4 h-4 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <span className="flex-1 min-w-0">{title}</span>
        </button>
        <div className="flex items-center gap-0.5 pr-3 pl-1 shrink-0">
          {features.copyText && <CopyButton text={recapText} />}
          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Réduire' : 'Développer'}
            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <span className="block transition-transform duration-200" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9660;</span>
          </button>
        </div>
      </div>
      {open && (
        <div className="px-5 pb-4 border-t border-slate-100">
          {/* Images */}
          {images && images.length > 0 && (
            <div className={`mt-3 flex flex-wrap gap-3 ${images.length === 1 ? 'justify-center' : ''}`}>
              {images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Image ${i + 1}`}
                  className="rounded-lg border border-slate-200 max-h-48 object-contain"
                />
              ))}
            </div>
          )}
          {/* Text */}
          {input && (
            <p className="mt-3 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{input}</p>
          )}
          {/* Context */}
          {context && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs font-medium text-slate-500 mb-1">Contexte suppl&eacute;mentaire</p>
              <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{context}</p>
            </div>
          )}
          {/* Label usage déclaré */}
          {labelUsageMeta && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs font-medium text-slate-500 mb-1">Usage déclaré</p>
              <p className="text-sm text-slate-600 leading-relaxed">{labelUsageMeta.label}</p>
            </div>
          )}
          {/* Événement / PLV déclaré */}
          {event && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
              {/* Méta opération */}
              {(event.eventName || event.eventLocation || event.eventDate) && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Op&eacute;ration</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {[event.eventName, event.eventLocation, event.eventDate].filter(Boolean).join(' · ')}
                  </p>
                </div>
              )}
              {/* Audience */}
              {audienceMeta && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Audience d&eacute;clar&eacute;e</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{audienceMeta.label}</p>
                </div>
              )}
              {/* Supports cochés + détails */}
              {event.supports.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1.5">Supports de l&apos;op&eacute;ration</p>
                  <ul className="space-y-2">
                    {event.supports.map((s) => {
                      const supportMeta = EVENT_SUPPORT_OPTIONS.find((o) => o.value === s);
                      const detail = event.supportDetails?.[s];
                      return (
                        <li key={s} className="text-sm">
                          <span className="font-medium text-slate-700">{supportMeta?.label ?? s}</span>
                          {detail && (
                            <p className="mt-0.5 text-slate-600 whitespace-pre-line leading-relaxed">{detail}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
